import { getCookieValue } from 'common/js/utils/cookie';
import {
    windowWithAds, initAd, displayAd, isNearViewport, lazyLoadAd,
} from './common/ads-essentials';
import { DESKTOP_MIN_WIDTH, TABLET_MIN_WIDTH } from './common/breakpoints';
import { EDITORIAL_CONTENT_AD_ID, editorialContentAd } from './common/editorial-content-ad';
import { homePageAd } from './home-page/home-page-ad';

const adIdFirstSponsored = 'cybernews_com_750x200_sponsorship_1';
const adIdArticleBottom = 'cybernews_com_responsive_bottom';
const adIdInArticleTop = 'cybernews_com_in_article_top';
const adIdMainEnd = 'cybernews_com_main_end_ad';
const adIdAsideTop = 'cybernews_com_aside_top_ad';
const homeAdIds = ['cybernews_com_home_ad_1', 'cybernews_com_home_ad_2', 'cybernews_com_home_ad_3', 'cybernews_com_home_ad_4'];
const HOME_FIRST_AD_POSITION = 1;
const cookieValue = getCookieValue('cookie_law');
const articleIsSponsored: boolean = !!document.getElementById(adIdFirstSponsored);

// parts/top-ad.twig holds the top ad's height from first paint (no layout shift on fill).
// Drop that once no ad can come there, so the empty wrapper collapses as before.
const releaseTopAdSpace = (): void => {
    document.querySelector('.editorial__top-ad_reserved')?.classList.remove('editorial__top-ad_reserved');
};

// No GPT at all without marketing consent, and sponsored articles never register the top slot.
if (cookieValue === 'cookieRejectMarketing' || articleIsSponsored) {
    releaseTopAdSpace();
}

if (cookieValue !== 'cookieRejectMarketing') {
    const mainScript = document.createElement('script');
    const scriptDoubleclick = document.createElement('script');
    const scriptStpd = document.createElement('script');

    windowWithAds.stpd = windowWithAds.stpd || { que: [] };

    mainScript.async = true;
    mainScript.crossOrigin = 'anonymous';
    mainScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5928161074779380';
    document.body.appendChild(mainScript);

    scriptDoubleclick.async = true;
    scriptDoubleclick.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

    scriptDoubleclick.onload = () => {
        windowWithAds.googletag = windowWithAds.googletag || { cmd: [] };
        windowWithAds.actualAds = [];

        if (articleIsSponsored) {
            if (window.innerWidth >= DESKTOP_MIN_WIDTH) {
                windowWithAds.actualAds.push(
                    {
                        id: 'cybernews_com_750x200_sponsorship_1',
                        slot: '/21924397842/cybernews.com_750x200_sponsorship_1',
                        dimensions: [[728, 90], [750, 200]],
                    },
                    {
                        id: 'cybernews_com_750x200_sponsorship_2',
                        slot: '/21924397842/cybernews.com_750x200_sponsorship_2',
                        dimensions: [[728, 90], [750, 200]],
                    },
                    {
                        id: 'cybernews_com_750x200_sponsorship_3',
                        slot: '/21924397842/cybernews.com_750x200_sponsorship_3',
                        dimensions: [[728, 90], [750, 200]],
                    },
                );
            } else {
                windowWithAds.actualAds.push(
                    {
                        id: 'cybernews_com_750x200_sponsorship_1',
                        slot: '/21924397842/cybernews.com_300x250_sponsorship_1',
                        dimensions: [[300, 250]],
                    },
                    {
                        id: 'cybernews_com_750x200_sponsorship_2',
                        slot: '/21924397842/cybernews.com_300x250_sponsorship_2',
                        dimensions: [[300, 250]],
                    },
                    {
                        id: 'cybernews_com_750x200_sponsorship_3',
                        slot: '/21924397842/cybernews.com_300x250_sponsorship_3',
                        dimensions: [[300, 250]],
                    },
                );
            }
        } else {
            // Top in-article ad — host article only (the slot div is rendered above the
            // breadcrumbs, outside .editorial__layout, so infinite scroll never appends
            // it). Same fixed div id across viewports; only the GAM ad unit changes. Not
            // rendered at all when a direct-sold creative takes the position
            // (pages/editorial/parts/direct-ads.twig), hence the id check.
            if (document.getElementById(adIdInArticleTop)) {
                if (window.innerWidth >= DESKTOP_MIN_WIDTH) {
                    windowWithAds.actualAds.push({
                        id: adIdInArticleTop,
                        slot: '/21924397842/cybernews.com_970x250_desktop_in_article_1',
                        dimensions: [970, 250],
                    });
                } else if (window.innerWidth >= TABLET_MIN_WIDTH) {
                    windowWithAds.actualAds.push({
                        id: adIdInArticleTop,
                        // Actual creative size is 728x90 (the 750x200_1 unit serves a
                        // 728x90 leaderboard on tablet).
                        slot: '/21924397842/cybernews.com_750x200_1',
                        dimensions: [728, 90],
                    });
                } else {
                    windowWithAds.actualAds.push({
                        id: adIdInArticleTop,
                        slot: '/21924397842/cybernews.com_300x250_article_mobile_1',
                        dimensions: [300, 250],
                    });
                }
            }

            // Extra ad at the end of .editorial__main — tablet + mobile only, where the
            // sticky sidebar ad stack is hidden. Lives inside .editorial__main, so it is
            // present on every article (host here; appended copies are re-registered by
            // infinite-scroll/ads.ts). Not added on desktop — the sidebar stack covers it,
            // and on appended desktop articles registerFragmentAds drops the empty wrapper.
            // Also configured on editorial pages without the host slot (ad-free article,
            // DEV-23440) so appended articles still get it.
            const hasMainEnd = document.getElementById(adIdMainEnd) || document.querySelector('.editorial__layout');

            if (hasMainEnd && window.innerWidth < DESKTOP_MIN_WIDTH) {
                if (window.innerWidth >= TABLET_MIN_WIDTH) {
                    windowWithAds.actualAds.push({
                        id: adIdMainEnd,
                        // Actual creative size is 728x90 (the 750x200_1 unit serves a
                        // 728x90 leaderboard on tablet).
                        slot: '/21924397842/cybernews.com_750x200_1',
                        dimensions: [728, 90],
                    });
                } else {
                    windowWithAds.actualAds.push({
                        id: adIdMainEnd,
                        slot: '/21924397842/cybernews.com_300x250_article_desktop_left_1',
                        dimensions: [300, 250],
                    });
                }
            }

            // Ad above editors-choice at the top of the sidebar — desktop only, host
            // article only (cleanFragment strips it from infinite-scroll appended
            // articles). GAM unit cybernews.com_300x600_sidebar_1 at 300x600 (same unit
            // the sticky sidebar stack reuses — see editorial-sidebar-ads/builder.ts).
            // Not rendered when a direct-sold "corner" creative takes the position
            // (pages/editorial/parts/direct-ads.twig), hence the id check.
            if (document.getElementById(adIdAsideTop) && window.innerWidth >= DESKTOP_MIN_WIDTH) {
                windowWithAds.actualAds.push({
                    id: adIdAsideTop,
                    slot: '/21924397842/cybernews.com_300x600_sidebar_1',
                    dimensions: [300, 600],
                });
            }

            if (document.getElementById(adIdArticleBottom)) {
                const isDesktop = window.innerWidth >= DESKTOP_MIN_WIDTH;

                windowWithAds.actualAds.push({
                    id: adIdArticleBottom,
                    slot: isDesktop
                        ? '/21924397842/cybernews.com_750x200_bottom'
                        : '/21924397842/cybernews.com_300x250_article_mobile_bottom',
                    dimensions: isDesktop
                        ? [[728, 90], [750, 200]]
                        : [[300, 250], [336, 280]],
                });
            }

            if (window.innerWidth >= TABLET_MIN_WIDTH) {
                windowWithAds.actualAds.push({
                    id: 'cybernews_com_300x600_sidebar_1',
                    slot: '/21924397842/cybernews.com_300x600_sidebar_1',
                    dimensions: [[300, 250], [300, 600]],
                });
            }

            /*
             * Home page ads -- one per between-category slot. The ids only exist in the DOM
             * on the home page, so this no-ops elsewhere.
             *
             * Dedicated cybernews.com_*_homepage_* units (970x250 desktop / 728x90 tablet /
             * 300x250 mobile), 5 deep per viewport -- see homePageAd().
             *
             * Positions are fixed per slot, not sequential over what happens to render, so
             * a slot that is conditionally absent (sponsored topics taking its place) does
             * not move the slots below it between units run to run.
             */
            homeAdIds.forEach((id, index) => {
                if (document.getElementById(id)) {
                    const position = index + HOME_FIRST_AD_POSITION;

                    windowWithAds.actualAds.push({
                        id,
                        ...homePageAd(position, window.innerWidth),
                    });
                }
            });

            if (document.querySelector('.editorial__layout')) {
                // Editorial has a single in-content ad; its GAM unit rotates by article
                // position across the infinite scroll. The host is position 1; appended
                // articles increment (infinite-scroll/ads.ts → registerFragmentAds
                // recomputes per appended index). Desktop 970x250_desktop_in_article_1/2,
                // tablet 750x200_1..3 (728x90), mobile 300x250_article_mobile_1..3.
                windowWithAds.actualAds.push({
                    id: EDITORIAL_CONTENT_AD_ID,
                    ...editorialContentAd(1, window.innerWidth),
                });
            } else if (window.innerWidth >= DESKTOP_MIN_WIDTH) {
                windowWithAds.actualAds.push(
                    {
                        id: 'cybernews_com_article_responsive_1',
                        slot: '/21924397842/cybernews.com_750x200_1',
                        dimensions: [[728, 90], [750, 200]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_2',
                        slot: '/21924397842/cybernews.com_750x200_2',
                        dimensions: [[728, 90], [750, 200]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_3',
                        slot: '/21924397842/cybernews.com_750x200_3',
                        dimensions: [[728, 90], [750, 200]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_4',
                        slot: '/21924397842/cybernews.com_750x200_4',
                        dimensions: [[728, 90], [750, 200]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_5',
                        slot: '/21924397842/cybernews.com_750x200_5',
                        dimensions: [[728, 90], [750, 200]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_6',
                        slot: '/21924397842/cybernews.com_750x200_6',
                        dimensions: [[728, 90], [750, 200]],
                    },
                );
            } else {
                windowWithAds.actualAds.push(
                    {
                        id: 'cybernews_com_article_responsive_1',
                        slot: '/21924397842/cybernews.com_300x250_article_mobile_1',
                        dimensions: [[300, 250], [336, 280]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_2',
                        slot: '/21924397842/cybernews.com_300x250_article_mobile_2',
                        dimensions: [[300, 250], [336, 280]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_3',
                        slot: '/21924397842/cybernews.com_300x250_article_mobile_3',
                        dimensions: [[300, 250], [336, 280]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_5',
                        slot: '/21924397842/cybernews.com_300x250_article_mobile_5',
                        dimensions: [[300, 250], [336, 280]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_4',
                        slot: '/21924397842/cybernews.com_300x250_article_mobile_4',
                        dimensions: [[300, 250], [336, 280]],
                    },
                    {
                        id: 'cybernews_com_article_responsive_6',
                        slot: '/21924397842/cybernews.com_300x250_article_mobile_6',
                        dimensions: [[300, 250], [336, 280]],
                    },
                );
            }
        }

        windowWithAds.googletag?.cmd.push(() => {
            for (const ad of windowWithAds.actualAds) {
                const element = document.getElementById(ad.id);

                // Slots near the viewport join Setupad's initial auction as before;
                // the rest wait until the reader scrolls near them.
                if (element && !isNearViewport(element)) {
                    lazyLoadAd(ad);
                } else if (element) {
                    initAd(ad);
                    displayAd(ad);
                }
            }

            googletag.pubads().disableInitialLoad();
            googletag.pubads().enableSingleRequest();
            googletag.pubads().collapseEmptyDivs();
            googletag.pubads().addEventListener('slotRenderEnded', (event) => {
                if (event.isEmpty && event.slot.getSlotElementId() === adIdInArticleTop) {
                    releaseTopAdSpace();
                }
            });

            if (windowWithAds.contentBucket) {
                googletag.pubads().setTargeting(
                    'Category',
                    [windowWithAds.contentBucket],
                );
            }

            googletag.enableServices();
        });

        windowWithAds.adsInitCompleted = true;
        document.dispatchEvent(new Event('gpt_init'));
    };

    // Blocked (e.g. by an ad blocker): GPT never renders, and without stpd the initial
    // ad request (disableInitialLoad above) is never made either.
    scriptDoubleclick.onerror = releaseTopAdSpace;
    document.head.appendChild(scriptDoubleclick);

    scriptStpd.async = true;
    scriptStpd.onerror = releaseTopAdSpace;
    scriptStpd.src = 'https://stpd.cloud/saas/5774';

    document.head.appendChild(scriptStpd);
}

export {};
